"use client";

import { AD_LIBRARY_LABEL } from "@/lib/ads/types";
import { mzstaticIcon } from "@/lib/mzstatic-image";
import * as React from "react";
import Image from '@/components/ui/image';
import Link from "next/link";
import { Check, Tag, HardDrive, Globe, Shield, Plus as MuiPlus, Share2 as MuiShare, Megaphone, Smartphone, Users } from "lucide-react";
import { FaAppStoreIos } from "react-icons/fa";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { MainCard } from "@/components/ui/main-card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { createDeveloperSlug, formatFileSize } from "@/lib/developer-utils";
import { countryCodeToFlagEmoji } from "@/lib/countries";
import { formatExactDate } from "@/lib/app-detail-utils";
import { formatPrice } from "@/lib/app-detail/price-history";
import type { AdNetworkSummary } from "@/lib/app-detail/overview";
import { Watermark } from "@/components/ui/watermark";
import { useThemeConfig } from "@/hooks/use-theme-config";
import { openLoginDialog } from "@/hooks/use-monitors";
import { buttonVariants } from "@/components/ui/button-variants";

const monthYear = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", year: "numeric" });

const NOISE_SVG = `data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.05'/%3E%3C/svg%3E`;

function GrainButton({ children, className, ...props }: React.ComponentProps<"div">) {
  return (
    <div 
      className={cn(
        "relative overflow-hidden rounded-sm border-0 shadow-sm ring-1 ring-inset ring-black/5 dark:ring-white/5 bg-sidebar hover:bg-sidebar-accent transition-colors cursor-pointer", 
        className
      )} 
      {...props}
    >
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: `url("${NOISE_SVG}")` }} />
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-white/80 to-transparent dark:from-white/[0.03] dark:to-transparent" />
      <div className="relative z-10 flex items-center justify-center w-full h-full">
        {children}
      </div>
    </div>
  );
}

function formatReleaseDays(daysAgo: number): string {
  if (daysAgo < 180) {
    return `${daysAgo}d ago`;
  } else if (daysAgo < 365) {
    const months = Math.floor(daysAgo / 30);
    return `${months}mo ago`;
  } else {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
}

type AppHeaderUnifiedProps = {
  appIcon: string | null;
  appName: string;
  subtitle?: string | null;
  developerName: string;
  developerId?: number; // Artist ID for creating developer page link
  appStoreUrl: string | null;
  releaseDaysAgo: number | null;
  lastReleaseDaysAgo: number | null;
  releaseDate: string | null;
  lastReleaseDate: string | null;
  genres?: string[] | null;
  fileSizeBytes?: string | null;
  languages?: string[] | null;
  contentRating?: string | null;
  // Overview parity: every category, Sensor Tower top countries, the ad libraries the app runs in.
  categories?: string[] | null;
  topCountries?: string[] | null;
  advertisedOn?: AdNetworkSummary[] | null;
  /** When the Sensor Tower row behind the top countries was fetched; shown as a title on that row. */
  sourceAsOf?: string | null;
  /** True while the overview route is still answering; the two rows keep their height so the tiles below do not move. */
  isLoadingOverview?: boolean;
  /** The newest price change inside the last 90 days (SPEC 26); null or absent hides the pill. */
  priceChange?: { after: number; date: string; currency: string; rose: boolean } | null;
  /** What the app runs on, from fields app_st already stores (SPEC 31). Only the true ones draw. */
  platforms?: { os: string | null; ipad: boolean; appleWatch: boolean; imessage: boolean } | null;
  isLoadingMetadata: boolean;
  isMobile: boolean;
  // Variant-specific props
  variant?: 'main' | 'embed';
  // Main-specific
  isMonitored?: boolean;
  user?: any;
  addPending?: boolean;
  removePending?: boolean;
  mobileTab?: string;
  onMonitorToggle?: () => void;
  onShowGate?: () => void;
  onShareOpen?: () => void;
  competitorsHref?: string;
  // Embed-specific
  appId?: number;
  countryCode?: string;
  appHref?: string;
};


// Derived values shared across header variants
type HeaderShared = {
  showTerminal: boolean;
  exactReleaseDate: string;
  exactUpdateDate: string;
  formattedSize: string | null;
};

/** A quiet pill action for the hero header. `primary` is the one filled button. */
function HeaderPill({ primary = false, showTerminal, className, children }: { primary?: boolean; showTerminal: boolean; className?: string; children: React.ReactElement<{ className?: string }> }) {
  const pill = cn(
    buttonVariants({ size: "pill", variant: primary ? "default" : "secondary" }),
    "gap-1.5",
    showTerminal && "border border-emerald-500/40 bg-black font-mono text-[11px] uppercase tracking-wider text-emerald-400 hover:bg-black hover:border-emerald-400/70 hover:text-emerald-300",
    className,
  );
  return React.cloneElement(children, { className: cn(pill, children.props.className) });
}

/** Track / Tracking. Signed out, it opens the login dialog. */
function TrackButton({ showTerminal, isMonitored, user, busy, onMonitorToggle, className }: {
  showTerminal: boolean; isMonitored?: boolean; user?: unknown; busy?: boolean; onMonitorToggle?: () => void; className?: string;
}) {
  return (
    <HeaderPill primary showTerminal={showTerminal} className={className}>
      <button
        type="button"
        aria-pressed={!!isMonitored}
        disabled={busy}
        onClick={() => {
          if (!user) return openLoginDialog();
          onMonitorToggle?.();
        }}
      >
        {isMonitored ? <Check size={15} aria-hidden /> : <MuiPlus size={15} aria-hidden />}
        <span>{isMonitored ? "Tracking" : "Track"}</span>
      </button>
    </HeaderPill>
  );
}

const AD_SHORT_LABEL: Record<string, string> = { apple: "Apple Ads" };

function DateFact({ label, daysAgo, exact, showTerminal }: { label: string; daysAgo: number; exact: string; showTerminal: boolean }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help whitespace-nowrap">
          {label} <span className={cn("font-medium text-foreground", showTerminal && "text-emerald-300")} suppressHydrationWarning>{formatReleaseDays(daysAgo)}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent className={cn(showTerminal && "bg-black border-emerald-500/50")}>
        <p className={cn(showTerminal && "font-mono text-emerald-300")}>{exact}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function MainHeader({
  appIcon,
  appName,
  subtitle,
  developerName,
  developerId,
  appStoreUrl,
  releaseDaysAgo,
  lastReleaseDaysAgo,
  genres,
  languages,
  contentRating,
  categories,
  topCountries,
  advertisedOn,
  sourceAsOf,
  isLoadingOverview,
  isLoadingMetadata,
  isMonitored,
  user,
  addPending,
  removePending,
  onMonitorToggle,
  onShareOpen,
  competitorsHref,
  priceChange,
  platforms,
  shared,
}: Pick<AppHeaderUnifiedProps,
  'appIcon' | 'appName' | 'subtitle' | 'developerName' | 'developerId' | 'appStoreUrl' |
  'releaseDaysAgo' | 'lastReleaseDaysAgo' | 'genres' | 'languages' | 'contentRating' |
  'categories' | 'topCountries' | 'advertisedOn' | 'sourceAsOf' | 'isLoadingOverview' | 'priceChange' | 'platforms' |
  'isLoadingMetadata' | 'isMobile' | 'isMonitored' | 'user' | 'addPending' | 'removePending' |
  'onMonitorToggle' | 'onShareOpen' | 'competitorsHref'
> & { shared: HeaderShared }) {
  // Every category when the Sensor Tower row lists them, else the iTunes genres.
  const categoryPills = categories && categories.length > 0 ? categories : genres || [];
  const { showTerminal, exactReleaseDate, exactUpdateDate, formattedSize } = shared;
  // `phone: false` facts are hidden below sm so the line fits once beside the icon: first category, size, age rating.
  const facts = [
    ...categoryPills.map((text, index) => ({ text, phone: index === 0 })),
    { text: formattedSize, phone: true },
    { text: languages && languages.length > 0 ? `${languages.length} ${languages.length === 1 ? "language" : "languages"}` : null, phone: false },
    { text: contentRating, phone: true },
  ].filter((fact): fact is { text: string; phone: boolean } => Boolean(fact.text));
  const developerClass = cn(
    "w-fit text-[14px] font-medium transition-colors",
    showTerminal ? "text-emerald-400 hover:text-emerald-300" : "text-foreground/80 hover:text-foreground hover:underline"
  );
  return (
    // A grid, so on every width the facts sit in the name column right under the developer line
    // instead of taking full rows below the icon. The wrapper is a size container for the actions below.
    <div className="@container mb-4 md:mb-5">
    <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3.5 sm:gap-x-6 lg:grid-cols-[auto_minmax(0,1fr)_auto]">
      <div className="contents">
        <div className="relative row-span-2 h-[88px] w-[88px] flex-shrink-0 self-start overflow-hidden rounded-[22px] bg-background shadow-[0_18px_40px_-20px_rgba(0,0,0,0.45)] ring-1 ring-black/[0.06] dark:ring-white/[0.08] sm:h-28 sm:w-28 sm:rounded-[28px] lg:h-[132px] lg:w-[132px] lg:rounded-[32px]">
          {isLoadingMetadata ? (
            <Skeleton className="h-full w-full bg-sidebar-lighter" />
          ) : appIcon ? (
            <Image
              src={mzstaticIcon(appIcon)}
              alt={appName || "App icon"}
              fill
              className="object-cover"
              unoptimized
              sizes="128px"
              priority
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-sidebar-lighter">
              <span className="text-5xl">📱</span>
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 sm:pt-1">
          <p className={cn("text-[22px] font-semibold leading-[1.05] tracking-[-0.025em] sm:text-[30px] lg:text-[36px]", showTerminal && "text-emerald-300 [text-shadow:0_0_10px_rgba(52,211,153,0.4)]")}>{appName}</p>
          {/* Desktop only: on a phone the name, developer, facts and dates already fill the space beside the icon. */}
          {subtitle && <p className="mt-0.5 hidden text-base leading-5 text-muted-foreground sm:line-clamp-2">{subtitle}</p>}
          <div className="mt-0.5 leading-5">
            {developerId ? (
              <Link href={`/developer/${createDeveloperSlug(developerName, developerId)}`} className={developerClass}>
                {developerName || "Unknown Developer"}
              </Link>
            ) : (
              <span className={developerClass}>{developerName || "Unknown Developer"}</span>
            )}
          </div>
        </div>
      </div>

      <div className="col-start-2 mt-1 flex flex-col gap-1 text-[11px] leading-4 text-muted-foreground sm:mt-2 sm:gap-1.5 sm:text-[12px]">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {facts.map((fact, index) => (
            <span key={fact.text} className={cn("items-center gap-x-2", fact.phone ? "flex" : "hidden sm:flex")}>
              {index > 0 && <span aria-hidden className="text-muted-foreground/40">·</span>}
              <span className="whitespace-nowrap">{fact.text}</span>
            </span>
          ))}
          <PricePill change={priceChange} className="gap-1 rounded-full bg-muted/70 px-2 py-px" textClass="text-[11px]" iconClass="w-2.5 h-2.5" />
          <PlatformPills platforms={platforms} className="gap-1 rounded-full bg-muted/70 px-2 py-px" textClass="text-[11px]" iconClass="w-2.5 h-2.5" />
        </div>
        {/* One line on every width: short labels, the full text in each tooltip; the ad networks give way first. */}
        <div className="flex min-w-0 items-center gap-x-3 whitespace-nowrap">
          {lastReleaseDaysAgo !== null && <DateFact label="Upd" daysAgo={lastReleaseDaysAgo} exact={`Updated ${exactUpdateDate}`} showTerminal={showTerminal} />}
          {releaseDaysAgo !== null && <DateFact label="Rel" daysAgo={releaseDaysAgo} exact={`Released ${exactReleaseDate}`} showTerminal={showTerminal} />}
          {isLoadingOverview && !topCountries && !advertisedOn && <Skeleton className="h-3 w-40 bg-sidebar-lighter" />}
          {topCountries && topCountries.length > 0 && (
            <span className="flex shrink-0 items-center gap-1" title={`Top countries: ${topCountries.join(", ")}${sourceAsOf ? `, fetched ${formatExactDate(sourceAsOf)}` : ""}`}>
              <Globe className="h-3 w-3" aria-label="Top countries" />
              <span className="text-foreground/80">{topCountries.map(countryCodeToFlagEmoji).join(" ")}</span>
            </span>
          )}
          {advertisedOn && advertisedOn.length > 0 && (
            <span className="flex min-w-0 items-center gap-1" title={`Advertised on ${advertisedOn.map((a) => `${AD_LIBRARY_LABEL[a.platform] ?? a.platform}: ${a.count.toLocaleString("en-US")} ads${a.firstSeenAt ? ` since ${monthYear(a.firstSeenAt)}` : ""}`).join("; ")}`}>
              <Megaphone className="h-3 w-3 shrink-0" aria-label="Advertised on" />
              <span className="truncate text-foreground/80">{advertisedOn.map((a) => AD_SHORT_LABEL[a.platform] ?? AD_LIBRARY_LABEL[a.platform] ?? a.platform).join(" · ")}</span>
            </span>
          )}
        </div>
      </div>
      {/* Desktop actions. md: classes, not isMobile, so phones never get them in the server HTML.
          Under the facts on md; on lg they move into the empty space right of the name so the stat cards sit higher:
          one row when the header is wide, else stacked one per row so the name keeps its width. */}
      <div className="col-start-2 mt-2.5 hidden items-center gap-2 md:flex lg:col-start-3 lg:row-span-2 lg:row-start-1 lg:mt-0 lg:flex-col lg:items-end lg:self-start lg:pt-1 lg:@5xl:flex-row">
        <TrackButton showTerminal={showTerminal} isMonitored={isMonitored} user={user} busy={addPending || removePending} onMonitorToggle={onMonitorToggle} />
        {competitorsHref && (
          <HeaderPill showTerminal={showTerminal}>
            <Link href={competitorsHref} data-testid="see-competitors">
              <Users size={15} aria-hidden />
              <span>Competitors</span>
            </Link>
          </HeaderPill>
        )}
        {/* The two icon buttons share a row even when the rest is stacked. */}
        <div className="flex items-center gap-2">
          <HeaderPill showTerminal={showTerminal} className="w-10 px-0">
            <button type="button" onClick={onShareOpen} title="Share" aria-label="Share">
              <MuiShare size={15} />
            </button>
          </HeaderPill>
          {appStoreUrl && (
            <HeaderPill showTerminal={showTerminal} className="w-10 px-0">
              <a href={appStoreUrl} target="_blank" rel="noopener noreferrer" title="Open in App Store" aria-label="Open in App Store">
                <FaAppStoreIos size={16} />
              </a>
            </HeaderPill>
          )}
        </div>
      </div>
    </div>
    </div>
  );
}

function EmbedHeader({
  appIcon,
  appName,
  subtitle,
  developerName,
  developerId,
  releaseDaysAgo,
  lastReleaseDaysAgo,
  genres,
  languages,
  contentRating,
  isLoadingMetadata,
  variant,
  appTrackerUrl,
  shared,
}: Pick<AppHeaderUnifiedProps,
  'appIcon' | 'appName' | 'subtitle' | 'developerName' | 'developerId' |
  'releaseDaysAgo' | 'lastReleaseDaysAgo' | 'genres' | 'languages' | 'contentRating' |
  'isLoadingMetadata' | 'variant'
> & { appTrackerUrl: string | null; shared: HeaderShared }) {
  const { showTerminal, exactReleaseDate, exactUpdateDate, formattedSize } = shared;
  return (
        <div className="mb-4">
          <div className="relative flex items-center gap-4 mb-2">
            <div className="relative h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28 lg:h-32 lg:w-32 rounded-[1.5rem] overflow-hidden flex-shrink-0 shadow-sm ring-1 ring-inset ring-black/[0.02] dark:ring-white/[0.02] bg-background group">
              {isLoadingMetadata ? (
                <Skeleton className="h-full w-full bg-sidebar-lighter rounded-[1.5rem]" />
              ) : appIcon ? (
                <>
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 z-10" />
                  <Image
                    src={mzstaticIcon(appIcon)}
                    alt={appName || "App icon"}
                    fill
                    className="object-cover"
                    unoptimized
                    sizes="128px"
                    priority
                  />
                </>
              ) : (
                <div className="h-full w-full bg-sidebar-lighter flex items-center justify-center">
                  <span className="text-7xl">📱</span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 mb-0.5">
                {isLoadingMetadata ? (
                  <Skeleton className="bg-sidebar-lighter h-6 sm:h-7 md:h-7 lg:h-7 w-48" />
                ) : (
                  <h2 className={cn("font-bold truncate", "text-xl sm:text-2xl md:text-2xl lg:text-2xl")}>{appName}</h2>
                )}
              </div>
              {subtitle && (
                <p className="text-muted-foreground text-sm mb-1 truncate">{subtitle}</p>
              )}
              <div className="flex flex-col gap-2">
                {isLoadingMetadata ? (
                  <Skeleton className="bg-sidebar-lighter h-4 w-32" />
                ) : developerId ? (
                  <Link
                    href={`/developer/${createDeveloperSlug(developerName, developerId)}`}
                    className={cn(
                      "inline-flex items-center rounded-full transition-colors text-[11px] sm:text-xs md:text-sm lg:text-md w-fit",
                      showTerminal ? "text-emerald-400 hover:text-emerald-300" : "text-blue-300 hover:text-blue-400"
                    )}
                  >
                    {developerName || "Unknown Developer"}
                  </Link>
                ) : (
                  <span className={cn(
                    "inline-flex items-center rounded-full text-[11px] sm:text-xs md:text-sm lg:text-md",
                    showTerminal ? "text-emerald-400" : "text-blue-300"
                  )}>
                    {developerName || "Unknown Developer"}
                  </span>
                )}
                
                {/* Pills */}
                <div className="flex flex-wrap gap-2">
                  {genres && genres.length > 0 && genres.slice(0, 2).map((genre) => (
                    <div key={genre} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-[#0b0d10] border border-black/[0.08] dark:border-white/[0.08] flex-shrink-0">
                      <Tag className="w-3 h-3 text-black/[0.45] dark:text-white/[0.45]" />
                      <span className="text-xs text-black/[0.75] dark:text-white/[0.75]">{genre}</span>
                    </div>
                  ))}
                  {Boolean(formattedSize) && (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-[#0b0d10] border border-black/[0.08] dark:border-white/[0.08] flex-shrink-0">
                      <HardDrive className="w-3 h-3 text-black/[0.45] dark:text-white/[0.45]" />
                      <span className="text-xs text-black/[0.75] dark:text-white/[0.75]">{formattedSize}</span>
                    </div>
                  )}
                  {languages && languages.length > 0 && (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-[#0b0d10] border border-black/[0.08] dark:border-white/[0.08] flex-shrink-0">
                      <Globe className="w-3 h-3 text-black/[0.45] dark:text-white/[0.45]" />
                      <span className="text-xs text-black/[0.75] dark:text-white/[0.75]">{languages.length} {languages.length === 1 ? 'language' : 'languages'}</span>
                    </div>
                  )}
                  {contentRating && (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-[#0b0d10] border border-black/[0.08] dark:border-white/[0.08] flex-shrink-0">
                      <Shield className="w-3 h-3 text-black/[0.45] dark:text-white/[0.45]" />
                      <span className="text-xs text-black/[0.75] dark:text-white/[0.75]">{contentRating}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Desktop actions - Embed variant only */}
            {variant === 'embed' && (
              <div className="hidden md:flex flex-col items-end gap-1 absolute right-0 top-0">
                <div className="flex flex-wrap gap-1 mb-1">
                  <div className="appstoretracker-branding">
                    <Watermark className="text-xs" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <GrainButton className="h-8 w-8">
                    <a
                      href={appTrackerUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View on AppStoreTracker"
                      aria-label="View on AppStoreTracker"
                      className="text-muted-foreground hover:text-sidebar-accent-foreground w-full h-full flex items-center justify-center"
                    >
                      <FaAppStoreIos size={16} />
                    </a>
                  </GrainButton>
                </div>
              </div>
            )}
            
            {/* Desktop date metrics */}
            <div className="hidden md:flex absolute right-0 bottom-0 items-center gap-4 text-xs">
              {lastReleaseDaysAgo !== null && (
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-[11px] mb-0.5 text-right">Updated</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-semibold text-foreground border-b border-dashed border-muted-foreground/30 pb-0.5 cursor-help">
                        {formatReleaseDays(lastReleaseDaysAgo)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{exactUpdateDate}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
              {releaseDaysAgo !== null && (
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-[11px] mb-0.5 text-right">Released</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-semibold text-foreground border-b border-dashed border-muted-foreground/30 pb-0.5 cursor-help">
                        {formatReleaseDays(releaseDaysAgo)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{exactReleaseDate}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
          
          {/* Mobile row - Embed variant only */}
          {variant === 'embed' && (
            <div className="md:hidden flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <div className="appstoretracker-branding">
                <Watermark className="text-xs" />
              </div>
              <GrainButton className="h-7 w-7">
                <a
                  href={appTrackerUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View on AppStoreTracker"
                  aria-label="View on AppStoreTracker"
                  className="text-muted-foreground hover:text-sidebar-accent-foreground w-full h-full flex items-center justify-center"
                >
                  <FaAppStoreIos size={14} />
                </a>
              </GrainButton>
            </div>
            <div className="flex items-center gap-4 text-xs">
              {lastReleaseDaysAgo !== null && (
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-[11px] mb-0.5 text-right">Updated</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-semibold text-foreground text-xs border-b border-dashed border-muted-foreground/30 pb-0.5">
                        {formatReleaseDays(lastReleaseDaysAgo)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{exactUpdateDate}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
              {releaseDaysAgo !== null && (
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-[11px] mb-0.5 text-right">Released</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-semibold text-foreground text-xs border-b border-dashed border-muted-foreground/30 pb-0.5">
                        {formatReleaseDays(releaseDaysAgo)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{exactReleaseDate}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
  );
}

function MobileInfoRow({
  appStoreUrl,
  isMonitored,
  user,
  addPending,
  removePending,
  onMonitorToggle,
  onShareOpen,
  competitorsHref,
  shared,
}: Pick<AppHeaderUnifiedProps,
  'appStoreUrl' | 'isMonitored' | 'user' |
  'addPending' | 'removePending' | 'onMonitorToggle' | 'onShareOpen' | 'competitorsHref'
> & { shared: HeaderShared }) {
  const { showTerminal } = shared;
  // This row only renders on a phone: compact 34 px pills so the cards and screenshots stay above the fold.
  return (
    <div className="-mt-1 mb-3">
      <div className="flex items-center gap-1.5">
        <TrackButton showTerminal={showTerminal} isMonitored={isMonitored} user={user} busy={addPending || removePending} onMonitorToggle={onMonitorToggle} className="h-[34px] flex-1 text-[13px]" />
        {competitorsHref && (
          <HeaderPill showTerminal={showTerminal} className="h-[34px] flex-1 text-[13px]">
            <Link href={competitorsHref} data-testid="see-competitors">
              <Users size={15} aria-hidden />
              <span>Competitors</span>
            </Link>
          </HeaderPill>
        )}
        <HeaderPill showTerminal={showTerminal} className="h-[34px] w-[34px] shrink-0 px-0">
          <button type="button" onClick={onShareOpen} title="Share" aria-label="Share">
            <MuiShare size={15} />
          </button>
        </HeaderPill>
        {appStoreUrl && (
          <HeaderPill showTerminal={showTerminal} className="h-[34px] w-[34px] shrink-0 px-0">
            <a href={appStoreUrl} target="_blank" rel="noopener noreferrer" title="Open in App Store" aria-label="Open in App Store">
              <FaAppStoreIos size={16} />
            </a>
          </HeaderPill>
        )}
      </div>
    </div>
  );
}

export function AppHeaderUnified(props: AppHeaderUnifiedProps) {
  const {
    releaseDate,
    lastReleaseDate,
    fileSizeBytes,
    variant = 'main',
    isMobile,
    mobileTab,
    appId,
    countryCode,
  } = props;

  // Hydration-safe terminal mode detection
  const { isTerminal, mounted } = useThemeConfig();

  const showTerminal = mounted && isTerminal;

  const exactReleaseDate = React.useMemo(() => {
    if (!releaseDate) return '';
    try {
      return new Date(releaseDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  }, [releaseDate]);

  const exactUpdateDate = React.useMemo(() => {
    if (!lastReleaseDate) return '';
    try {
      return new Date(lastReleaseDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  }, [lastReleaseDate]);

  const formattedSize = formatFileSize(fileSizeBytes ?? undefined);

  const appTrackerUrl = variant === 'embed' && appId && countryCode
    ? props.appHref ?? `https://appstoretracker.com/app/app-${appId}?country=${countryCode}&ref=embed`
    : null;

  const isEmbedVariant = variant === 'embed';
  const showMain = variant === 'main' && (!isMobile || mobileTab === 'info');

  const shared: HeaderShared = { showTerminal, exactReleaseDate, exactUpdateDate, formattedSize };

  return (
    <TooltipProvider delayDuration={200}>
      {showMain && (
        <MainHeader
          appIcon={props.appIcon}
          appName={props.appName}
          subtitle={props.subtitle}
          developerName={props.developerName}
          developerId={props.developerId}
          appStoreUrl={props.appStoreUrl}
          releaseDaysAgo={props.releaseDaysAgo}
          lastReleaseDaysAgo={props.lastReleaseDaysAgo}
          genres={props.genres}
          languages={props.languages}
          contentRating={props.contentRating}
          categories={props.categories}
          topCountries={props.topCountries}
          advertisedOn={props.advertisedOn}
          sourceAsOf={props.sourceAsOf}
          isLoadingOverview={props.isLoadingOverview}
          priceChange={props.priceChange}
          platforms={props.platforms}
          isLoadingMetadata={props.isLoadingMetadata}
          isMobile={props.isMobile}
          isMonitored={props.isMonitored}
          user={props.user}
          addPending={props.addPending}
          removePending={props.removePending}
          onMonitorToggle={props.onMonitorToggle}
          onShareOpen={props.onShareOpen}
          competitorsHref={props.competitorsHref}
          shared={shared}
        />
      )}

      {isEmbedVariant && (
        <EmbedHeader
          appIcon={props.appIcon}
          appName={props.appName}
          subtitle={props.subtitle}
          developerName={props.developerName}
          developerId={props.developerId}
          releaseDaysAgo={props.releaseDaysAgo}
          lastReleaseDaysAgo={props.lastReleaseDaysAgo}
          genres={props.genres}
          languages={props.languages}
          contentRating={props.contentRating}
          isLoadingMetadata={props.isLoadingMetadata}
          variant={props.variant}
          appTrackerUrl={appTrackerUrl}
          shared={shared}
        />
      )}

      {/* md:hidden, not isMobile: the server cannot know the width, so a JS check
          inserted this row after hydration and shoved the page down on phones. */}
      {variant === 'main' && mobileTab === 'info' && (
        <div className="md:hidden">
        <MobileInfoRow
          appStoreUrl={props.appStoreUrl}
          isMonitored={props.isMonitored}
          user={props.user}
          addPending={props.addPending}
          removePending={props.removePending}
          onMonitorToggle={props.onMonitorToggle}
          onShareOpen={props.onShareOpen}
          competitorsHref={props.competitorsHref}
          shared={shared}
        />
        </div>
      )}
    </TooltipProvider>
  );
}

/** A price move in the last 90 days. Absent, and the app's price has not moved recently. */
function PricePill({ change, className, textClass, iconClass }: { change?: { after: number; date: string; currency: string; rose: boolean } | null; className: string; textClass: string; iconClass: string }) {
  if (!change) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={cn("inline-flex items-center rounded-full flex-shrink-0 cursor-default", className)} data-testid="price-pill" aria-label={`Price changed to ${formatPrice(change.after, change.currency)}`}>
          <Tag className={cn(iconClass, "text-black/[0.45] dark:text-white/[0.45]")} />
          <span className={cn(textClass, "text-black/[0.75] dark:text-white/[0.75]")}>Price {change.rose ? "↑" : "↓"} {formatPrice(change.after, change.currency)}</span>
          <span className={cn(textClass, "text-black/[0.5] dark:text-white/[0.5]")}>· {formatExactDate(`${change.date}T00:00:00Z`, { utc: true })}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        This app changed price in this storefront on {formatExactDate(`${change.date}T00:00:00Z`, { utc: true })}. The price history card lower down lists every change we hold.
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * What the app ships on, from `app_st.st_raw`: `os`, `tablet_screenshot_urls`,
 * `apple_watch_enabled`, `imessage_enabled`. Every one of these was carried as far
 * as the search result row and dropped unread until SPEC 31. This claims no iPad
 * RANKING - `app_rankings` holds no top-free-ipad row and the collector zone owns that.
 */
function PlatformPills({ platforms, className, textClass, iconClass }: { platforms?: { os: string | null; ipad: boolean; appleWatch: boolean; imessage: boolean } | null; className: string; textClass: string; iconClass: string }) {
  if (!platforms) return null;
  const labels = [
    platforms.os ? platforms.os.toLowerCase() === 'ios' ? 'iOS' : platforms.os : null,
    platforms.ipad ? 'iPad' : null,
    platforms.appleWatch ? 'Apple Watch' : null,
    platforms.imessage ? 'iMessage' : null,
  ].filter((l): l is string => !!l);
  if (labels.length === 0) return null;
  return (
    <>
      {labels.map((label) => (
        <div key={label} className={cn("inline-flex items-center rounded-full flex-shrink-0", className)} data-testid={`platform-pill-${label.toLowerCase().replace(/\s+/g, '-')}`}>
          <Smartphone className={cn(iconClass, "text-black/[0.45] dark:text-white/[0.45]")} />
          <span className={cn(textClass, "text-black/[0.75] dark:text-white/[0.75]")}>{label}</span>
        </div>
      ))}
    </>
  );
}

